import { useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Line, TransformControls as DreiTransformControls } from '@react-three/drei';
import * as THREE from 'three';
import type { ThreeEvent } from '@react-three/fiber';
import useSceneStore from '../../stores/sceneStore';
import { useRoamStore } from '../../stores/roamStore';
import useSelectionStore from '../../stores/selectionStore';
import { cameraControlsRegistry } from '../../stores/cameraControlsStore';
import { syncHelperCameraInGroup } from '../../utils/pathCameraHelper';
import type { PathCamera, CameraKeyFrame } from '../../types/designer';

// ============================================
// 轨道相机（路径相机）Rig —— 编辑器内可视化 + 播放驱动
// - 路径线：CatmullRom 样条（或线性折线），黄色
// - 关键帧控制点：黄色小球，可拖拽改位置（手写指针拖拽，绕相机平面移动）
// - 相机实体 + CameraHelper 视锥线框（橙色）：
//   左键点选（2026-10-15 P0-④ §3.5 坐标轴化：仅选中，不再自定义拖拽）
//   选中后挂 TransformControls gizmo（复用工具栏 平移/旋转 模式）→ 松手写回"最后一个关键帧"
// - 播放：沿路径插值 position/rotation/fov 驱动真实相机，锁定 OrbitControls
// ============================================

const FRAME_COLOR = '#ffd666';
const PATH_COLOR = '#f5c542';
const FRUSTUM_COLOR = '#ff8c00';

/** 生成路径曲线（catmullRom 平滑 / linear 折线） */
function buildCurve(keyFrames: CameraKeyFrame[], interpolation: 'linear' | 'catmullRom') {
  if (keyFrames.length < 2) return null;
  const points = keyFrames.map((f) => new THREE.Vector3(...f.position));
  if (interpolation === 'catmullRom') {
    return new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0.5);
  }
  const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom', 0);
  return curve;
}

/** 关键帧控制点：黄色小球，指针拖拽沿平行相机平面移动 */
function KeyFrameHandle({
  position,
  color,
  scale = 1,
  onSelect,
  onMove,
}: {
  position: [number, number, number];
  color: string;
  scale?: number;
  onSelect: () => void;
  onMove: (pos: [number, number, number]) => void;
}) {
  const { camera, gl } = useThree();
  const drag = useRef<{ plane: THREE.Plane; offset: THREE.Vector3 } | null>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  const ndc = (e: PointerEvent | MouseEvent) => {
    const rect = gl.domElement.getBoundingClientRect();
    return new THREE.Vector2(
      ((e.clientX - rect.left) / rect.width) * 2 - 1,
      -((e.clientY - rect.top) / rect.height) * 2 + 1,
    );
  };

  const handlePointerMove = (e: PointerEvent) => {
    if (!drag.current || !meshRef.current) return;
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc(e), camera);
    const hit = new THREE.Vector3();
    if (ray.ray.intersectPlane(drag.current.plane, hit)) {
      const p = hit.add(drag.current.offset);
      meshRef.current.position.copy(p);
      onMove([p.x, p.y, p.z]);
    }
  };

  const endDrag = () => {
    drag.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', endDrag);
  };

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    // 2026-10-14：关键帧点与相机重合（捕获点=当前视点）时不拦截指针，交还 OrbitControls 旋转视角
    const worldPos = meshRef.current?.getWorldPosition(new THREE.Vector3()) ?? new THREE.Vector3(...position);
    if (camera.position.distanceTo(worldPos) < 1.5) return;
    e.stopPropagation();
    onSelect();
    const ray = new THREE.Raycaster();
    ray.setFromCamera(ndc(e.nativeEvent), camera);
    const plane = new THREE.Plane();
    const normal = camera.getWorldDirection(new THREE.Vector3()).negate();
    const meshPos = meshRef.current?.getWorldPosition(new THREE.Vector3()) ?? new THREE.Vector3(...position);
    plane.setFromNormalAndCoplanarPoint(normal, meshPos);
    const hit = new THREE.Vector3();
    if (ray.ray.intersectPlane(plane, hit)) {
      drag.current = { plane, offset: meshPos.clone().sub(hit) };
    }
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', endDrag);
  };

  useEffect(() => () => endDrag(), []);

  return (
    <mesh
      ref={meshRef}
      position={position}
      scale={scale}
      onPointerDown={handlePointerDown}
      castShadow={false}
    >
      <sphereGeometry args={[0.38, 16, 16]} />
      <meshBasicMaterial color={color} />
    </mesh>
  );
}

/** 相机实体 + 视锥辅助（2026-10-15 P0-④ §3.5 坐标轴化）：
 * - 左键点选（仅选中，不再自定义拖拽；未选中时不拦截视角旋转）
 * - 选中后挂 TransformControls gizmo（复用工具栏 平移/旋转 模式）→ 松手写回最后一个关键帧
 */
function CameraEntity({
  cameraId,
  frame,
  frameIndex,
  active,
}: {
  cameraId: string;
  frame: CameraKeyFrame;
  frameIndex: number;
  active: boolean;
}) {
  const { camera } = useThree();
  const groupRef = useRef<THREE.Group>(null);
  const [gizmoObj, setGizmoObj] = useState<THREE.Group | null>(null);
  const cam = useMemo(() => new THREE.PerspectiveCamera(frame.fov, 1, 0.1, 2000), [frame.fov]);
  const helper = useMemo(() => new THREE.CameraHelper(cam), [cam]);
  const updateKeyFrame = useSceneStore((s) => s.updateKeyFrame);
  const isPathPlaying = useRoamStore((s) => s.isPathPlaying);
  const entitySelected = useRoamStore((s) => s.pathCameraEntitySelected);
  const setActivePathCamera = useRoamStore((s) => s.setActivePathCamera);
  const setPathCameraEntitySelected = useRoamStore((s) => s.setPathCameraEntitySelected);
  const transformMode = useSceneStore((s) => s.transformMode);

  const selectCamera = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    // 选中相机实体时清掉模型选择（相机 gizmo 与模型 gizmo 互斥）
    useSelectionStore.getState().deselectAll();
    setActivePathCamera(cameraId);
    setPathCameraEntitySelected(true);
  };

  // 2026-10-14（用户反馈：加了相机后视角转不动、多台相机都捕获在同一位置）：
  // 相机实体渲染在"捕获时的相机位置"——即用户当前视点处；此时左键拖拽会被实体自身拦截
  // （R3F 事件 stopPropagation 挡住 OrbitControls），导致视角无法旋转、后续捕获全落在同一点。
  // 因此：当相机与实体距离过近（用户就在实体处）时，实体不拦截指针事件，交还给 OrbitControls 旋转视角。
  const isAtViewpoint = useCallback(() => {
    const g = groupRef.current;
    if (!g) return false;
    return camera.position.distanceTo(g.getWorldPosition(new THREE.Vector3())) < 1.5;
  }, [camera]);

  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (isAtViewpoint()) return; // 用户就在实体处：不拦截，交给 OrbitControls 旋转视角
    selectCamera(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAtViewpoint, cameraId]);

  // 坐标轴松手 → 写回最后一个关键帧（position + rotation）
  const writeBackKeyFrame = useCallback(() => {
    const g = groupRef.current;
    if (!g) return;
    updateKeyFrame(cameraId, frameIndex, {
      position: [g.position.x, g.position.y, g.position.z],
      rotation: [g.rotation.x, g.rotation.y, g.rotation.z],
    });
  }, [cameraId, frameIndex, updateKeyFrame]);

  // 调试/测试桥（E2E）：坐标轴是否已挂载（§3.5「选中相机 → 坐标轴出现」断言）
  useEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    if (entitySelected && !isPathPlaying) {
      w.__pathCameraGizmoActive = true;
      return () => { w.__pathCameraGizmoActive = false; };
    }
    w.__pathCameraGizmoActive = false;
    return undefined;
  }, [entitySelected, isPathPlaying]);

  // 视锥实时跟随 group：拖拽时所见即所得（松手前 store 不写）
  // 2026-10-14 修复（用户反馈：漫游相机不在线条汇聚点 / 镜头方向不对）：
  // helper 是 group 子节点，而 three CameraHelper 以 `helper.matrix = cam.matrixWorld` 作本地矩阵——
  // 再把 group 位姿写进 cam 会与 group 变换叠加（位置×2、旋转²）→ 视锥顶点/朝向双重偏移。
  // 正确：cam 保持单位变换（group 本地系即相机系）→ 视锥顶点=机身中心、朝向=镜头方向。
  useFrame(() => {
    const g = groupRef.current;
    if (!g) return;
    syncHelperCameraInGroup(cam);
    helper.update();
  });

  return (
    <>
      <group
        ref={(r) => {
          groupRef.current = r;
          if (r !== gizmoObj) setGizmoObj(r);
        }}
        position={frame.position}
        rotation={frame.rotation}
        scale={entitySelected ? 1.15 : 1}
        onPointerDown={handlePointerDown}
      >
        {/* 2026-10-13（用户反馈 14）：机身居中——视锥线汇集中心=机身中心（此前机身前移 0.35 导致顶点偏后） */}
        <mesh position={[0, 0, 0]} castShadow={false}>
          <boxGeometry args={[0.7, 0.45, 0.7]} />
          <meshBasicMaterial color={entitySelected ? '#ffd666' : FRUSTUM_COLOR} transparent opacity={0.9} />
        </mesh>
        {/* 2026-10-14：镜头朝 -Z（相机观察方向）——与视锥展开方向/播放朝向一致（此前 +Z 与视锥反向） */}
        <mesh position={[0, 0, -0.55]} castShadow={false}>
          <boxGeometry args={[0.35, 0.3, 0.4]} />
          <meshBasicMaterial color="#333" />
        </mesh>
        {/* 2026-10-15（P0-④ §3.5）：加大点选命中区（透明不可见球）——实体小、远看难选中 */}
        <mesh position={[0, 0, 0]}>
          <sphereGeometry args={[1.4, 12, 12]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} />
        </mesh>
        {/* 视锥线框（仅激活路径显示，避免画面杂乱） */}
        {active && <primitive object={helper} />}
      </group>
      {/* 2026-10-15（P0-④ §3.5）：选中后坐标轴化编辑（平移/旋转，松手写回最后一个关键帧） */}
      {entitySelected && !isPathPlaying && gizmoObj && (
        <DreiTransformControls
          // 调试/测试桥（E2E）：暴露 TC 实例（同 window.__transformControls 先例）
          ref={(r: any) => { (window as unknown as Record<string, unknown>).__pathCameraTC = r; }}
          object={gizmoObj}
          mode={transformMode === 'scale' ? 'translate' : transformMode}
          onMouseUp={writeBackKeyFrame}
        />
      )}
    </>
  );
}

/** 单个路径相机的可视化（路径线 + 关键帧控制点 + 相机实体/视锥） */
function PathCameraView({ cameraData, active }: { cameraData: PathCamera; active: boolean }) {
  const showPathHelpers = useRoamStore((s) => s.showPathHelpers);
  const isPathPlaying = useRoamStore((s) => s.isPathPlaying);
  const selectedKeyFrameIndex = useRoamStore((s) => s.selectedKeyFrameIndex);
  const setSelectedKeyFrame = useRoamStore((s) => s.setSelectedKeyFrame);
  const setActivePathCamera = useRoamStore((s) => s.setActivePathCamera);
  const setPathCameraEntitySelected = useRoamStore((s) => s.setPathCameraEntitySelected);
  const entitySelected = useRoamStore((s) => s.pathCameraEntitySelected);
  const updateKeyFrame = useSceneStore((s) => s.updateKeyFrame);
  const curve = useMemo(
    () => buildCurve(cameraData.keyFrames, cameraData.interpolation),
    [cameraData.keyFrames, cameraData.interpolation],
  );
  const curvePoints = useMemo(() => (curve ? curve.getPoints(64) : []), [curve]);

  // 2026-10-13 修复（用户反馈 19"再次点击添加后黑屏"）：useEffect 必须在早退之前——此前 keyFrames 0→1 时
  // hooks 数量变化触发 React "Rendered more hooks" 错误→白/黑屏
  // 2026-10-13 修复（用户反馈 20"点击播放没有效果"）：播放时也必须锁定 OrbitControls，否则每帧被轨道控制覆盖→看不见播放
  useEffect(() => {
    if (active && (entitySelected || isPathPlaying)) {
      cameraControlsRegistry.setOrbitControlsEnabled?.(false);
    } else {
      cameraControlsRegistry.setOrbitControlsEnabled?.(true);
    }
  }, [entitySelected, active, isPathPlaying]);

  if (!showPathHelpers || cameraData.keyFrames.length === 0) return null;

  return (
    <group
      onClick={(e) => {
        e.stopPropagation();
        // 2026-10-15（P0-④ §3.5）：点击相机实体时（pointerdown 已选中实体）不重置实体选中——
        // setActivePathCamera 会把 pathCameraEntitySelected 置 false → 坐标轴刚出现就被取消
        if (useRoamStore.getState().pathCameraEntitySelected) return;
        setActivePathCamera(cameraData.id);
      }}
    >
      {/* 路径线 */}
      {curve && curvePoints.length > 1 && (
        <Line
          points={curvePoints}
          color={PATH_COLOR}
          lineWidth={2}
          transparent
          opacity={active ? 1 : 0.5}
          dashed
          dashSize={0.6}
          gapSize={0.35}
        />
      )}

      {/* 关键帧控制点（播放时隐藏；点选+拖拽编辑）
          2026-10-15（P0-④ §3.5）：激活路径的**最后一帧**与相机实体重合——不再渲染其控制点
          （实体是唯一操作目标，避免小球拦截点选/与实体 gizmo 冲突） */}
      {!isPathPlaying &&
        cameraData.keyFrames.map((frame, i) => {
          if (active && i === cameraData.keyFrames.length - 1) return null;
          const selected = active && selectedKeyFrameIndex === i;
          return (
            <KeyFrameHandle
              key={i}
              position={frame.position}
              color={selected ? '#ffa940' : FRAME_COLOR}
              scale={selected ? 1.3 : 1}
              onSelect={() => {
                setActivePathCamera(cameraData.id);
                setSelectedKeyFrame(i);
                setPathCameraEntitySelected(false);
              }}
              onMove={(pos) => updateKeyFrame(cameraData.id, i, { position: pos })}
            />
          );
        })}

      {/* 相机实体 + 视锥（仅激活路径显示；左键点实体拖拽平移，拖镜头前紫环旋转朝向） */}
      {active && (
        <CameraEntity
          cameraId={cameraData.id}
          frame={cameraData.keyFrames[cameraData.keyFrames.length - 1]}
          frameIndex={cameraData.keyFrames.length - 1}
          active={active}
        />
      )}
    </group>
  );
}

// ============================================
// 播放驱动：沿路径插值 position/rotation/fov
// ============================================
// ============================================
// 2026-10-16（P1 余项）：漫游相机正交播放——播放期间换上正交相机（退出还原编辑器相机）
// 视锥半高 = 注视距离 10m × tan(fov/2)（与「相机路径镜像」注视点语义一致）
// ============================================
function RoamOrthoRig() {
  const activePathCameraId = useRoamStore((s) => s.activePathCameraId);
  const isPathPlaying = useRoamStore((s) => s.isPathPlaying);
  const active = useSceneStore((s) => s.pathCameras.find((c) => c.id === activePathCameraId));
  const swap = active?.viewType === 'orthographic' && isPathPlaying;
  const { set, camera: defaultCamera, size } = useThree();
  const orthoRef = useRef<THREE.OrthographicCamera | null>(null);
  const prevRef = useRef<THREE.Camera | null>(null);

  useEffect(() => {
    if (!swap) return;
    const aspect = Math.max(1e-3, size.width / Math.max(1, size.height));
    const fov = active?.keyFrames?.[active.keyFrames.length - 1]?.fov ?? 50;
    const hh = Math.max(0.5, 10 * Math.tan((fov * Math.PI) / 360));
    const cam = new THREE.OrthographicCamera(-hh * aspect, hh * aspect, hh, -hh, 0.1, 20000);
    prevRef.current = prevRef.current ?? defaultCamera;
    orthoRef.current = cam;
    set({ camera: cam as never });
    cameraControlsRegistry.requestRender?.();
    return () => {
      orthoRef.current = null;
      if (prevRef.current) set({ camera: prevRef.current as never });
      cameraControlsRegistry.requestRender?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swap]);

  useEffect(() => {
    const cam = orthoRef.current;
    if (!cam) return;
    const aspect = Math.max(1e-3, size.width / Math.max(1, size.height));
    const hh = cam.top;
    cam.left = -hh * aspect;
    cam.right = hh * aspect;
    cam.updateProjectionMatrix();
  }, [size.width, size.height, swap]);

  return null;
}

function PathCameraPlayer() {
  const camera = useThree((s) => s.camera);
  const pathCameras = useSceneStore((s) => s.pathCameras);
  const cameraPathSpeed = useSceneStore((s) => s.cameraPathSpeed);
  const activePathCameraId = useRoamStore((s) => s.activePathCameraId);
  const isPathPlaying = useRoamStore((s) => s.isPathPlaying);
  const setPathPlaying = useRoamStore((s) => s.setPathPlaying);
  const setPlayTime = useRoamStore((s) => s.setPlayTime);
  const perspCamera = camera as THREE.PerspectiveCamera;

  const active = pathCameras.find((c) => c.id === activePathCameraId);
  // 2026-08-31｜旧格式兼容：demo-orbit-path 等关键帧只有 position/target/duration（缺 time/rotation/fov），
  // 用 duration 累计生成播放时间；rotation/fov 缺失按默认兜底，避免播放 NaN/崩溃
  const kfTimes = active && active.keyFrames.length > 0
    ? (() => {
        let acc = 0;
        return active.keyFrames.map((f: any) => {
          if (typeof f.time === 'number' && Number.isFinite(f.time)) return f.time;
          acc += (f.duration ?? 1);
          return acc;
        });
      })()
    : [];
  const totalTime = kfTimes.length > 0 ? Math.max(...kfTimes) : 0;

  useFrame((_, delta) => {
    if (!isPathPlaying || !active || active.keyFrames.length < 2) return;
    const kf = active.keyFrames;
    const times = kfTimes;
    let t = useRoamStore.getState().playTime + delta * cameraPathSpeed;
    if (t >= totalTime) {
      if (active.loop) {
        t = t % totalTime;
      } else {
        t = totalTime;
        setPathPlaying(false);
      }
    }
    setPlayTime(t);

    // 找到当前时间所在的两帧区间
    let i = 0;
    while (i < times.length - 2 && times[i + 1] <= t) i++;
    const a = kf[i] as any, b = kf[i + 1] as any;
    const span = Math.max(times[i + 1] - times[i], 0.001);
    const u = THREE.MathUtils.clamp((t - times[i]) / span, 0, 1);

    // position：catmullRom 走样条 / linear 直线
    if (active.interpolation === 'catmullRom') {
      const curve = buildCurve(kf, 'catmullRom');
      if (curve) {
        // 按弧长占比映射时间占比
        const segStart = times[i] / totalTime;
        const segEnd = times[i + 1] / totalTime;
        const uu = segStart + u * (segEnd - segStart);
        camera.position.copy(curve.getPointAt(THREE.MathUtils.clamp(uu, 0, 1)));
      }
    } else {
      const sa = new THREE.Vector3(...(a.position ?? [0, 0, 0]));
      const sb = new THREE.Vector3(...(b.position ?? [0, 0, 0]));
      camera.position.lerpVectors(sa, sb, u);
    }

    // rotation：四元数 slerp（旧格式缺 rotation 默认看向前方）
    const qa = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(a.rotation ?? [0, 0, 0])));
    const qb = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(b.rotation ?? [0, 0, 0])));
    camera.quaternion.slerpQuaternions(qa, qb, u);

    // fov（透视）/ 视锥半高（正交，P1 余项：正交播放）
    const isOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    const fovNow = (a.fov ?? 60) + ((b.fov ?? 60) - (a.fov ?? 60)) * u;
    if (isOrtho) {
      const o = camera as THREE.OrthographicCamera;
      const hh = Math.max(0.5, 10 * Math.tan((fovNow * Math.PI) / 360));
      const aspect = Math.max(1e-3, (o.right - o.left) / Math.max(1e-6, o.top - o.bottom));
      o.top = hh;
      o.bottom = -hh;
      o.left = -hh * aspect;
      o.right = hh * aspect;
      o.updateProjectionMatrix();
    } else {
      perspCamera.fov = fovNow;
      perspCamera.updateProjectionMatrix();
    }
  });

  return null;
}

// ============================================
// 入口组件：渲染全部路径相机 + 播放驱动
// ============================================
export default function PathCameraRig() {
  const pathCameras = useSceneStore((s) => s.pathCameras);
  const activePathCameraId = useRoamStore((s) => s.activePathCameraId);

  // 2026-10-13（用户反馈 13）：首次添加相机"加载很久"= 首次渲染 CameraHelper 触发的着色器/材质编译一次性开销。
  // 挂载时用隐藏探针预热编译（gl.compile），把该开销挪到进入编辑器时。
  const { gl, scene, camera } = useThree();
  useEffect(() => {
    const probe = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
    const helper = new THREE.CameraHelper(probe);
    helper.visible = false;
    scene.add(helper);
    try {
      (gl as unknown as { compile?: (s: THREE.Scene, c: THREE.Camera) => void }).compile?.(scene, camera);
    } catch {
      /* 预热失败不阻塞 */
    }
    scene.remove(helper);
  }, [gl, scene, camera]);

  return (
    <>
      {/* 2026-10-16（P1 余项）：正交漫游播放（播放期间换正交相机） */}
      <RoamOrthoRig />
      {pathCameras.map((c) => (
        <PathCameraView key={c.id} cameraData={c} active={c.id === activePathCameraId} />
      ))}
      <PathCameraPlayer />
    </>
  );
}

/** 供外部（漫游面板）请求"拾取当前编辑器视角生成关键帧" */
export function requestCapturePathFrame() {
  window.dispatchEvent(new CustomEvent('path-camera-capture'));
}

/** 捕获监听：需要相机，必须挂在 Canvas 内部 */
export function PathCameraCaptureBridge() {
  const camera = useThree((s) => s.camera);
  const addKeyFrame = useSceneStore((s) => s.addKeyFrame);

  useEffect(() => {
    const handler = () => {
      const id = useRoamStore.getState().activePathCameraId;
      const target = useSceneStore.getState().pathCameras.find((c) => c.id === id);
      if (!id || !target) return;
      const lastTime = target.keyFrames.length > 0
        ? Math.max(...target.keyFrames.map((f) => f.time))
        : 0;
      const frame: CameraKeyFrame = {
        time: Math.round((lastTime + 3) * 100) / 100,
        position: [camera.position.x, camera.position.y, camera.position.z],
        rotation: [camera.rotation.x, camera.rotation.y, camera.rotation.z],
        fov: (camera as THREE.PerspectiveCamera).fov,
      };
      addKeyFrame(id, frame);
    };
    window.addEventListener('path-camera-capture', handler);
    return () => window.removeEventListener('path-camera-capture', handler);
  }, [camera, addKeyFrame]);

  return null;
}